/** Cambio de clave movil con la misma politica de la web (Yup, con su test).
 *
 * Replica declarativa de `frontend/src/schemas/credentials.schemas.ts`
 * (`newPassword` exacto): minimo 10, maximo 128 y 72 bytes, al menos
 * 3 de 4 clases (minusculas, mayusculas, digitos, simbolos) y rechazo
 * de claves predecibles. El servidor es la autoridad; aqui solo se
 * evita descubrir los requisitos al enviar.
 */
import * as yup from 'yup';

/** Limite de bytes de bcrypt. Coincide con `BCRYPT_MAX_BYTES` del backend. */
const BCRYPT_MAX_BYTES = 72;

export const MOBILE_PASSWORD_RULES = {
  minLength: 10,
  maxLength: 128,
  minClasses: 3,
  charClasses: 4,
  maxBytes: BCRYPT_MAX_BYTES,
} as const;

const CLASS_LABELS = ['minúsculas', 'mayúsculas', 'dígitos', 'símbolos'];

function countClasses(value: string): number {
  return [
    /[a-z]/.test(value),
    /[A-Z]/.test(value),
    /[0-9]/.test(value),
    /[^A-Za-z0-9]/.test(value),
  ].filter(Boolean).length;
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function longestSequentialRun(value: string): number {
  const lowered = value.toLowerCase();
  let longest = 0;
  let current = 0;
  let delta = 0;
  let previous: string | null = null;
  for (const char of lowered) {
    const isAlnum = /[A-Za-z0-9]/.test(char);
    if (previous !== null && isAlnum && /[A-Za-z0-9]/.test(previous)) {
      const step = char.charCodeAt(0) - previous.charCodeAt(0);
      if ((step === 1 || step === -1) && (delta === 0 || step === delta)) {
        delta = step;
        current += 1;
      } else {
        delta = 0;
        current = 0;
      }
    } else {
      delta = 0;
      current = 0;
    }
    longest = Math.max(longest, current + 1);
    previous = char;
  }
  return longest;
}

const TRIVIAL_TOKENS = [
  'password',
  'contrasena',
  'contraseña',
  'clave',
  'qwerty',
  'asdfgh',
  'zxcvbn',
  'letmein',
  'welcome',
  'admin',
  'iloveyou',
  'abc',
  '1234',
];

export const MOBILE_TRIVIAL_MAX_DOMINANT_RATIO = 0.6;

export function isTrivialMobilePassword(value: string): boolean {
  const lowered = value.toLowerCase();
  if (TRIVIAL_TOKENS.some((token) => lowered.includes(token))) return true;
  if (longestSequentialRun(lowered) >= 4) return true;
  const unique = new Set(lowered).size;
  if (unique <= 1) return true;
  if (unique <= 2 && lowered.length > 8) return true;
  let dominant = 0;
  for (const char of new Set(lowered)) {
    dominant = Math.max(dominant, lowered.split(char).length - 1);
  }
  return dominant > lowered.length * MOBILE_TRIVIAL_MAX_DOMINANT_RATIO;
}

const mobileNewPassword = yup
  .string()
  .required('La contraseña es obligatoria')
  .test(
    'min-length',
    `La contraseña debe tener al menos ${MOBILE_PASSWORD_RULES.minLength} caracteres`,
    (value) => !value || value.length >= MOBILE_PASSWORD_RULES.minLength,
  )
  .test(
    'max-length',
    `La contraseña no puede superar ${MOBILE_PASSWORD_RULES.maxLength} caracteres`,
    (value) => !value || value.length <= MOBILE_PASSWORD_RULES.maxLength,
  )
  .test(
    'max-bytes',
    `La contraseña no puede superar ${MOBILE_PASSWORD_RULES.maxBytes} bytes`,
    (value) => !value || byteLength(value) <= MOBILE_PASSWORD_RULES.maxBytes,
  )
  .test(
    'classes',
    `Combina al menos ${MOBILE_PASSWORD_RULES.minClasses} de ${MOBILE_PASSWORD_RULES.charClasses} clases: ${CLASS_LABELS.join(', ')}`,
    (value) => !value || countClasses(value) >= MOBILE_PASSWORD_RULES.minClasses,
  )
  .test('not-trivial', 'Esta contraseña es demasiado predecible', (value) => {
    if (!value) return true;
    return !isTrivialMobilePassword(value);
  });

export const mobileChangePasswordSchema = yup.object({
  current_password: yup.string().required('La contraseña actual es obligatoria'),
  new_password: mobileNewPassword,
  confirm_password: yup
    .string()
    .required('Confirma la contraseña')
    .test('match', 'Las contraseñas no coinciden', function (value) {
      if (!value) return true;
      return value === this.parent['new_password'];
    }),
});

export type MobileChangePasswordForm = yup.InferType<typeof mobileChangePasswordSchema>;
